import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SubOperationsEnum } from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "./LocalEvent";
import { ServerEvent } from "./ServerEvent";

/**
 * BaseAction class adds request / response verification capability to be able to determine if received response belongs to a particular instance of action creator.
 */

interface IRequestExecutionContext {
    (serverOperation: number, subOperation: number, messageId: string, data: any, dto?: any): boolean;
}

interface IResponseExecutionContext {
    (response: ServerEvent, responseId: string, dto: any): boolean;
}

class ActionItem {
    public messageId: string;
    public serverOperation: number;
}

class BaseAction {
    private requestCollection: ActionItem[];

    private onBeforeExecute: IRequestExecutionContext;
    private onBeforeHandleResponse: IResponseExecutionContext;

    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        this.requestCollection = [];
        if (onBeforeExecute != null) {
            this.onBeforeExecute = onBeforeExecute;
        }
        if (onBeforeHandleResponse != null) {
            this.onBeforeHandleResponse = onBeforeHandleResponse;
        }
    }

    /**
     * @description Checks whether a response received by the store was invoked by current action instance.
     * @param {Messages.Base.BaseResponseMessage} response ServerResponseDispatcher response of type Messages.Base.BaseResponseMessage
     * @return {boolean}
     */
    public ShouldHandleResonse(response: ServerEvent): boolean {
        if (response == null) return false;
        return this.indexOfItem(response.MessageId, response.Operation) >= 0;
    }

    /**
     * Executes a handler if server response to action was initiated from this instance
     * @param {Messages.Base.BaseResponseMessage} response ServerResponseDispatcher response of type Messages.Base.BaseResponseMessage
     * @param {any} handler A function that will be called if response is expected to execute. Handler will receive a reference to original response. Ex: function(response){...}
     */
    public HandleResponseFor(response: ServerEvent, handler: any): void {
        if (this.ShouldHandleResonse(response)) {
            this.RemoveRequest(response.MessageId, response.Operation);
            handler(response);
        }
    }

    /**
     * Registers request to current action instance. Once registered, upon receiving a response, a verification can be made if action instance is the initiator.
     * @param {string} MessageId BaseRequest message id.
     */
    private RegisterRequest(messageId: string, operation: number): void {
        if ($.trim(messageId).length === 0) throw new TypeError("Message id is required");
        var idx = this.indexOfItem(messageId.toLowerCase(), operation);
        if (idx < 0) {
            var i = new ActionItem(); i.messageId = messageId.toLowerCase(); i.serverOperation = operation;
            this.requestCollection.push(i);
        }
    }

    private indexOfItem(messageId: string, operation: number): number {
        for (var i = 0; i < this.requestCollection.length; i++) {
            var e = this.requestCollection[i];
            if (e.messageId === messageId.toLowerCase() && e.serverOperation === operation) return i;
        }
        return -1;
    }

    // eslint-disable-next-line @typescript-eslint/ban-types -- using `Function` as the base for a generic type constraint is fine since the signature is specified
    public dispatch<TSuccessCallback extends Function = () => void>(serverOperation: ServerOperations.Server, data?: {}, then?: TSuccessCallback, hideDefaultLoading?: boolean, subOperation?: number): void {
        subOperation = subOperation || SubOperationsEnum.Default;

        var messageId = GuidHelper.NewGuid();
        this.RegisterRequest(messageId, serverOperation);
        var client = new Client();
        client.Execute(serverOperation, subOperation, messageId, data, this, hideDefaultLoading, this.onBeforeExecute, this.onBeforeHandleResponse, then);
    }

    /**
     * Send an asynchronous call to the server, and track the request ID for subsequent response handling.
     * @param serverOperation The server operation code to invoke.
     * @param requestData The data to send in the request.
     * @param subOperation The frontend sub-operation ID. This is never sent to the server, it's only used for response handling. The default value is {@link SubOperationsEnum.Default}.
     * @param hideDefaultLoading Whether to hide the default loading indicator. Default false.
     * @param onError An optional callback to invoke if the request failed (e.g. the server is unavailable). This is not called if the request succeeded but returns an error message.
     * @param onSuccess An optional callback to invoke after the response is received and processed. This is called even if the response contains an error message.
     * @param onComplete An optional callback to invoke after the response failed, whether it succeeded or failed.
     */
    public Execute(serverOperation: number, requestData?: Record<string, any>, subOperation?: number, hideDefaultLoading?: boolean, onError?: () => void, onSuccess?: () => void, onComplete?: () => void): void {
        var requestId = GuidHelper.NewGuid();
        subOperation ??= SubOperationsEnum.Default;

        this.RegisterRequest(requestId, serverOperation);
        var client = new Client();
        client.Execute(serverOperation, subOperation, requestId, requestData, this, hideDefaultLoading, this.onBeforeExecute, this.onBeforeHandleResponse, onSuccess, onError, onComplete);
    }

    /**
     * Send an asynchronous call to the server, and track the request ID for subsequent response handling. This is equivalent to {@link Execute}, but replaces callback arguments with a promise and throws an error if the request can't be sent.
     * @param serverOperation The server operation code to invoke.
     * @param requestData The data to send in the request.
     * @param subOperation The frontend sub-operation ID. This is never sent to the server, it's only used for response handling. The default value is {@link SubOperationsEnum.Default}.
     * @param hideDefaultLoading Whether to hide the default loading indicator. Default false.
     */
    public ExecuteAsync(serverOperation: number, requestData?: Record<string, any>, subOperation?: number, hideDefaultLoading?: boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.Execute(
                serverOperation,
                requestData,
                subOperation,
                hideDefaultLoading,
                reject,
                null,
                resolve
            );
        });
    }

    public ExecuteDSQ(serverOperation: number, data?: any, subOperation?: number, hideDefaultLoading?: boolean, onResponseHandlerSuccess?: () => void): void {
        var self = this;
        var messageId = GuidHelper.NewGuid();
        if (subOperation == null) subOperation = 0;

        this.RegisterRequest(messageId, serverOperation);

        var WrappedOnBeforeResponse: IResponseExecutionContext = (response, responseId, dto) => {
            var errors: ErrorMessage[] = response.Errors;

            if (errors.length > 0) {
                var HasResetNipErrors = errors.some(err => {
                    if (err == null)
                        return false;

                    return err.Code === "ClearNIPCache";
                });
                var HasNoKeyErrors = errors.some(err => {
                    if (err == null)
                        return false;

                    return err.Code === "KeyNotConnected";
                });

                if (HasResetNipErrors == true) {
                    sessionStore.DSQPin = "";
                }

                if (HasNoKeyErrors == true) {
                    // special case for no key detected
                    sessionStore.showPopup({
                        content: "La clé DSQ ne semble pas branchée.",
                        showCancel: true,

                        okLabel: "Désactiver le DSQ et continuer",
                        okAction: () => sessionStore.DSQPrescriptionDisabled = true
                    });
                }
                else {
                    // show DSQ errors
                    sessionStore.showPopup(errors);
                }
            }

            if (self.onBeforeHandleResponse != null)
                return self.onBeforeHandleResponse(response, responseId, dto);
            else
                return null;
        };

        var client = new Client();
        var action: () => void = () => {
            data.PIN = sessionStore.DSQPin;
            client.Execute(serverOperation, subOperation, messageId, data, self, hideDefaultLoading, self.onBeforeExecute, WrappedOnBeforeResponse, onResponseHandlerSuccess);
        };

        if (sessionStore.hasDsqNIP()) {
            action();
        }
        else {
            let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplayDSQNip);
            op.AddItem("action", action);
            MainComponentStateDispatcher.dispatch(op);
        }
    }

    /**
     * ExecuteGlobal is identical to Execute EXCEPT it does not register internally MessageId
     * @param serverOperation Backend operation id
     * @param requestDto json payload
     * @param subOperation sub-operation id (never sent to the server, copied to response) SubOperations.Default as default value
     * @param hideDefaultLoading overrides MYLE loading indicator
     */
    public ExecuteGlobal(serverOperation: number, requestDto?: any, subOperation?: number, hideDefaultLoading?: boolean, then?: () => void): void {
        var requestId = GuidHelper.NewGuid();
        if (subOperation == null) subOperation = 0;
        let client = new Client();
        client.Execute(serverOperation, subOperation, requestId, requestDto, this, hideDefaultLoading, null, null, then);
    }

    public RemoveRequest(messageId: string, operation: number): void {
        var index = this.indexOfItem(messageId, operation);
        if (index >= 0) { this.requestCollection.splice(index, 1); }
    }
}

export { BaseAction, IRequestExecutionContext, IResponseExecutionContext };
